import { useEffect, useState, type RefObject } from 'react';
import styled from '@emotion/styled';
import { designSystem } from '@/styles/theme';
import { useListHeaderHeight } from '@/components/common/organism/ListPageLayout';

interface Step {
    step: number;
    value: string;
}

interface StickyStepStripProps {
    stepData: Step[];
    target: number;
    /** 이 요소(큰 단계 표시)가 화면 위로 사라지면 띠를 보인다 */
    watchRef: RefObject<HTMLElement>;
}

/*
 * 긴 단계 폼에서 스크롤해도 지금 몇 단계인지 보이게 하는 얇은 띠 — 머리줄 바로 아래에 붙는다(2026-10-11 회원가입).
 * 큰 단계 표시는 제자리에 두고 이 띠만 붙인다: 큰 표시를 붙였다 줄이면 문서 높이가 바뀌어 화면이 덜컹인다.
 * position: fixed 금지(UI 가이드 5장 — #app-content 가 당겨서 새로고침 때 transform 되면 튄다) — 높이 0 짜리 sticky 자리에 띠를 겹쳐 그린다.
 * 감싼 흰 틀의 좌우 안쪽 여백만큼 띠를 넓혀 틀 끝까지 칠한다(--strip-bleed).
 */
const STRIP_HEIGHT_PX: number = 44;

/*
 * 자리는 띠 높이 그대로 두고 아래 여백을 그만큼 빼 문서 흐름에서는 0 — 공용 입력 처리(inputKeyboard·scrollBelowStickyBars)가
 * 화면 위 고정 줄 높이를 이 요소의 크기로 재기 때문(높이 0 이면 띠를 못 보고 칸을 띠 뒤로 올렸다, 2026-10-11 아이폰).
 * 띠가 숨어 있을 때는 누름이 아래 칸으로 지나가게 pointer-events 를 끈다.
 */
const Anchor = styled.div`
    position: sticky;
    z-index: ${designSystem.zIndex.sticky};
    height: ${STRIP_HEIGHT_PX}px;
    margin-bottom: -${STRIP_HEIGHT_PX}px;
    width: 100%;
    pointer-events: none;
`;

const Strip = styled.div<{ shown: boolean }>`
    position: absolute;
    top: 0;
    left: calc(-1 * var(--strip-bleed, 16px));
    right: calc(-1 * var(--strip-bleed, 16px));
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 12px;
    height: ${STRIP_HEIGHT_PX}px;
    padding: 0 var(--strip-bleed, 16px);
    pointer-events: ${(p) => (p.shown ? 'auto' : 'none')};
    background: #fff;
    border-bottom: 1px solid #e3e8e6;
    opacity: ${(p) => (p.shown ? 1 : 0)};
    visibility: ${(p) => (p.shown ? 'visible' : 'hidden')};
    transition: opacity 0.15s ease;
    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

const Inner = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
`;

const Dots = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const Dot = styled.span<{ status: 'done' | 'now' | 'todo' }>`
    width: ${(p) => (p.status === 'now' ? 18 : 8)}px;
    height: 8px;
    border-radius: 4px;
    background: ${(p) => (p.status === 'todo' ? '#d3dbd8' : designSystem.colors.primary)};
`;

const Label = styled.span`
    min-width: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1f2a28;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Count = styled.span`
    margin-right: 6px;
    font-weight: 600;
    color: ${designSystem.colors.primary};
`;

const StickyStepStrip = ({ stepData, target, watchRef }: StickyStepStripProps) => {
    const headerHeight: number = useListHeaderHeight(true);
    const [shown, setShown] = useState<boolean>(false);

    useEffect(() => {
        const element: HTMLElement | null = watchRef.current;
        if (!element || typeof IntersectionObserver === 'undefined') return;
        // 큰 단계 표시가 머리줄 밑으로 다 들어가면 띠를 보인다
        const observer = new IntersectionObserver(
            ([entry]) => setShown(!entry.isIntersecting && entry.boundingClientRect.top < headerHeight + 1),
            { rootMargin: `-${headerHeight}px 0px 0px 0px`, threshold: 0 },
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, [watchRef, headerHeight]);

    const current: Step | undefined = stepData.find((item) => item.step === target);
    return (
        <Anchor style={{ top: headerHeight }} aria-hidden={!shown}>
            <Strip shown={shown} role="status" aria-label={current ? `${stepData.length}단계 중 ${target}단계 ${current.value}` : undefined}>
                <Inner>
                    <Dots>
                        {stepData.map((item) => (
                            <Dot key={item.step} status={item.step < target ? 'done' : item.step === target ? 'now' : 'todo'} />
                        ))}
                    </Dots>
                    <Label>
                        <Count>{target}/{stepData.length}</Count>
                        {current?.value}
                    </Label>
                </Inner>
            </Strip>
        </Anchor>
    );
};

export default StickyStepStrip;
